// Web Vitals first-party.
// - Conecta o reportWebVitals.js (estava órfão) à pipeline de telemetria.
// - Cada métrica vira logUxEvent('web_vital', { name, value, rating, route }).
// - Para CLS, captura também o seletor do elemento que mais contribuiu
//   (largestShiftSource).
// - Para INP (attribution build), captura interactionTarget (elemento que
//   o usuário interagiu) + interactionType (click/keydown/pointerdown) +
//   inputDelay/processingDuration/presentationDelay (breakdown da latência).
//   Sem isso só sabemos "INP p75 = 400ms" mas não "qual botão é lento".
// - Para LCP (attribution build), captura element + elementSelector + url do
//   recurso (imagem) + breakdown de tempo (TTFB, resourceLoadDelay, etc).
// - Compatível com web-vitals v5.x (onCLS/onINP/onLCP/onFCP/onTTFB). FID foi
//   descontinuado e substituído por INP a partir da v3.

import reportWebVitals from '../reportWebVitals';
import { logUxEvent } from './uxLogger';

// Mesma heurística de seletor usada em friction.js (rage/dead clicks).
// Inline aqui para evitar dependência circular entre módulos de telemetria.
//
// Auditoria 13/05: 206 CLS sources foram capturados como simplesmente "button"
// (sem classe, sem texto identificavel). Adicionamos aria-label e contexto
// do parent para distinguir botoes internos do Stream Chat / componentes UI.
const describeNode = (el) => {
  try {
    if (!el || el.nodeType !== 1) return 'unknown';
    const tag = (el.tagName || 'div').toLowerCase();
    const id = el.getAttribute && el.getAttribute('id');
    const testId = el.getAttribute && (el.getAttribute('data-testid') || el.getAttribute('data-test'));
    if (testId) return `${tag}[data-testid="${testId}"]`;
    if (id && !/^react-|^radix-|^headlessui-/i.test(id)) return `${tag}#${id}`;
    let cls = '';
    if (el.classList && el.classList.length) {
      cls = '.' + Array.from(el.classList).slice(0, 2).join('.');
    }
    let text = (el.textContent || '').trim().slice(0, 30);
    // Botoes com so SVG/icone nao tem textContent — usar aria-label.
    if (!text) {
      const ariaLabel = el.getAttribute && el.getAttribute('aria-label');
      if (ariaLabel) text = `[${ariaLabel.slice(0, 30)}]`;
    }
    let suffix = `${tag}${cls}${text ? `:"${text}"` : ''}`;
    // Se nem cls nem text foram extraidos (selector ficou igual a tag pura),
    // adiciona contexto do parent para distinguir botoes genericos.
    if (suffix === tag && el.parentElement) {
      const p = el.parentElement;
      let pCls = '';
      if (p.classList && p.classList.length) {
        pCls = '.' + Array.from(p.classList).slice(0, 1).join('.');
      }
      const pId = p.getAttribute && p.getAttribute('id');
      const pTag = (p.tagName || 'div').toLowerCase();
      const pSig = pId && !/^react-|^radix-/i.test(pId) ? `#${pId}` : pCls;
      if (pSig) suffix = `${pTag}${pSig}>${tag}`;
    }
    return suffix;
  } catch {
    return 'unknown';
  }
};

// Para uma métrica de CLS, retorna o seletor do nó com maior contribuição
// (somando shifts dos quais ele foi a fonte principal). Cada `LayoutShift`
// entry tem .sources[] com .node + previousRect/currentRect; o primeiro
// source é tipicamente o elemento que mais se moveu.
const findLargestShiftSource = (entries) => {
  if (!Array.isArray(entries) || !entries.length) return null;
  const byNode = new Map();
  for (const entry of entries) {
    if (!entry || entry.hadRecentInput) continue;
    const sources = Array.isArray(entry.sources) ? entry.sources : [];
    for (const src of sources) {
      const sel = describeNode(src && src.node);
      if (!sel || sel === 'unknown') continue;
      byNode.set(sel, (byNode.get(sel) || 0) + (entry.value || 0));
    }
  }
  if (!byNode.size) return null;
  let bestSel = null;
  let bestVal = 0;
  for (const [sel, val] of byNode.entries()) {
    if (val > bestVal) { bestVal = val; bestSel = sel; }
  }
  return bestSel;
};

// Limiares "good/needs-improvement/poor" do web.dev. Usados para classificar P75.
const RATING_THRESHOLDS = {
  CLS:  { good: 0.1,  poor: 0.25 },
  LCP:  { good: 2500, poor: 4000 },
  INP:  { good: 200,  poor: 500 },
  FCP:  { good: 1800, poor: 3000 },
  TTFB: { good: 800,  poor: 1800 },
};

const rate = (name, value) => {
  const t = RATING_THRESHOLDS[name];
  if (!t || typeof value !== 'number') return 'unknown';
  if (value <= t.good) return 'good';
  if (value <= t.poor) return 'needs-improvement';
  return 'poor';
};

// Para INP, o attribution.interactionTarget eh o elemento alvo. Mas se nao
// vier, tentamos achar pelo eventEntry.target ou pelo describeNode dele.
const describeInpTarget = (attribution) => {
  if (!attribution) return null;
  // attribution.interactionTarget é o seletor (string) na v5+
  if (typeof attribution.interactionTarget === 'string' && attribution.interactionTarget) {
    return attribution.interactionTarget.slice(0, 120);
  }
  // Fallback: elemento bruto
  const el =
    attribution.interactionTargetElement ||
    (attribution.processedEventEntries && attribution.processedEventEntries[0] && attribution.processedEventEntries[0].target);
  if (el) return describeNode(el);
  return null;
};

const handleEntry = (metric) => {
  if (!metric) return;
  const { name, value, id, delta, entries, attribution } = metric;
  if (!name || typeof value !== 'number') return;
  const rounded = name === 'CLS' ? Math.round(value * 1000) / 1000 : Math.round(value);
  const payload = {
    name,
    value: rounded,
    delta: typeof delta === 'number' ? Math.round(delta * 1000) / 1000 : null,
    rating: rate(name, value),
    metric_id: id || null,
    route: typeof window !== 'undefined' ? window.location.pathname : null,
  };
  if (name === 'CLS') {
    const source = findLargestShiftSource(entries);
    if (source) payload.largest_shift_source = source;
    if (attribution && typeof attribution.largestShiftTarget === 'string') {
      payload.cls_largest_shift_target = attribution.largestShiftTarget.slice(0, 120);
    }
  }
  if (name === 'INP' && attribution) {
    const target = describeInpTarget(attribution);
    if (target) payload.inp_target = target;
    if (typeof attribution.interactionType === 'string') payload.inp_type = attribution.interactionType;
    if (typeof attribution.inputDelay === 'number') payload.inp_input_delay = Math.round(attribution.inputDelay);
    if (typeof attribution.processingDuration === 'number') payload.inp_processing_ms = Math.round(attribution.processingDuration);
    if (typeof attribution.presentationDelay === 'number') payload.inp_presentation_ms = Math.round(attribution.presentationDelay);
    if (typeof attribution.loadState === 'string') payload.inp_load_state = attribution.loadState;
  }
  if (name === 'LCP' && attribution) {
    if (typeof attribution.target === 'string') payload.lcp_target = attribution.target.slice(0, 120);
    if (typeof attribution.url === 'string') {
      // Mantém só origin+pathname, sem query/hash, para evitar PII.
      try {
        const u = new URL(attribution.url, typeof window !== 'undefined' ? window.location.origin : 'http://x');
        payload.lcp_url = (u.origin + u.pathname).slice(0, 200);
      } catch {
        payload.lcp_url = attribution.url.slice(0, 200);
      }
    }
    if (typeof attribution.timeToFirstByte === 'number') payload.lcp_ttfb_ms = Math.round(attribution.timeToFirstByte);
    if (typeof attribution.resourceLoadDelay === 'number') payload.lcp_resource_load_delay_ms = Math.round(attribution.resourceLoadDelay);
    if (typeof attribution.resourceLoadDuration === 'number') payload.lcp_resource_load_ms = Math.round(attribution.resourceLoadDuration);
    if (typeof attribution.elementRenderDelay === 'number') payload.lcp_element_render_delay_ms = Math.round(attribution.elementRenderDelay);
  }
  logUxEvent('web_vital', payload);
};

let initialized = false;
export const initWebVitals = () => {
  if (initialized) return;
  initialized = true;
  try {
    reportWebVitals(handleEntry);
  } catch (err) {
    // Falha silenciosa: não queremos derrubar app por causa de telemetria.
    if (typeof console !== 'undefined') console.warn('[telemetry] webVitals init falhou', err);
  }
};
